import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Lock,
  LogOut,
  Phone,
  UserRound,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { trackEvent } from '../../lib/analytics';
import CasinoThemePicker from '../../components/CasinoThemePicker';

export default function SettingsTab({ user, checkAuth, goBack, onLogout }) {
  const [activePanel, setActivePanel] = useState(null);
  const [username, setUsername] = useState(user?.username || '');
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [dateOfBirth, setDateOfBirth] = useState(user?.date_of_birth || user?.dob || '');
  const [phoneNumber, setPhoneNumber] = useState(user?.phone_number || user?.phone || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const phoneVerified = Boolean(user?.phone_verified || user?.phone_verified_at);
  const activePanelTitle = activePanel === 'profile'
    ? 'Profile Information'
    : activePanel === 'phone'
      ? 'Phone Number'
      : activePanel === 'password'
        ? 'Change Password'
        : 'Settings';

  useEffect(() => {
    setUsername(user?.username || '');
    setFullName(user?.full_name || '');
    setDateOfBirth(user?.date_of_birth || user?.dob || '');
    setPhoneNumber(user?.phone_number || user?.phone || '');
  }, [user?.username, user?.full_name, user?.date_of_birth, user?.dob, user?.phone_number, user?.phone]);

  const handleSaveProfile = async () => {
    const nextUsername = username.trim();
    if (!nextUsername || nextUsername.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    setSavingProfile(true);
    try {
      await api.patch('/api/auth/profile', {
        username: nextUsername,
        full_name: fullName.trim(),
        date_of_birth: dateOfBirth || '',
      });
      trackEvent('profile_information_updated', { source: 'settings' });
      toast.success('Profile information saved');
      await checkAuth?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile information');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePhone = async () => {
    const cleanedPhone = phoneNumber.trim();
    if (!cleanedPhone) {
      toast.error('Enter your phone number');
      return;
    }
    setSavingPhone(true);
    try {
      const currentPhone = (user?.phone_number || user?.phone || '').trim();
      const alreadyVerifiedSamePhone = phoneVerified && cleanedPhone === currentPhone;
      await api.patch('/api/auth/profile', { phone_number: cleanedPhone });
      if (!alreadyVerifiedSamePhone) {
        await api.post('/api/users/me/phone-verification/request', { phone: cleanedPhone });
      }
      trackEvent('phone_number_submitted', { source: 'settings' });
      toast.success(alreadyVerifiedSamePhone ? 'Phone number saved.' : 'Verification request submitted.');
      await checkAuth?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail?.message || detail || err.response?.data?.message || 'Failed to submit phone verification');
    } finally {
      setSavingPhone(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Fill in all password fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await api.post('/api/auth/change-password', { old_password: currentPassword, new_password: newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      trackEvent('password_changed', { source: 'settings' });
      toast.success('Password changed successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div
      className="vl-pirate-page vl-settings-page min-h-full px-4 py-3 pb-24 text-white vl-readable-player"
      data-testid="settings-tab"
    >
      <div className="max-w-2xl mx-auto space-y-3">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={activePanel ? () => setActivePanel(null) : goBack}
            className="vl-settings-back w-10 h-10 rounded-full flex items-center justify-center"
            data-testid="settings-back"
            aria-label="Back"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-black text-[#F5D76E]">{activePanelTitle}</h1>
          <div className="w-10" />
        </div>

        {!activePanel && (
        <div className="space-y-3" data-testid="settings-option-list">
          <CasinoThemePicker />
          <SettingsOption
            icon={UserRound}
            title="Profile Information"
            subtitle="Username, full name, date of birth"
            active={activePanel === 'profile'}
            onClick={() => setActivePanel('profile')}
          />
          <SettingsOption
            icon={Phone}
            title="Phone Number"
            subtitle={phoneVerified ? 'Verified number saved' : 'Add or update your number'}
            status={phoneVerified ? 'Verified' : null}
            active={activePanel === 'phone'}
            onClick={() => setActivePanel('phone')}
          />
          <SettingsOption
            icon={Lock}
            title="Change Password"
            subtitle="Update your account password"
            active={activePanel === 'password'}
            onClick={() => setActivePanel('password')}
          />
          <button
            type="button"
            onClick={onLogout}
            className="vl-wood-setting-row vl-wood-setting-row--danger w-full rounded-[22px] px-4 py-3 flex items-center gap-3 text-left text-red-200"
            data-testid="logout-btn"
          >
            <span className="vl-wood-setting-row__icon w-10 h-10 rounded-xl flex items-center justify-center">
              <LogOut className="w-5 h-5" />
            </span>
            <span className="flex-1">
              <span className="block text-base font-black">Sign Out</span>
              <span className="block text-xs text-red-100/75">Logout from this device</span>
            </span>
          </button>
        </div>
        )}

        {activePanel && (
          <div className="space-y-3" data-testid="settings-detail-page">
        {activePanel === 'profile' && (
          <SettingsPanel title="Profile Information" icon={UserRound} testId="settings-profile-panel">
            <LabelledField label="Username">
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="playername"
                className="bg-[#0B0E14] border-white/10 text-base rounded-2xl"
                data-testid="settings-username"
              />
            </LabelledField>
            <LabelledField label="Full Name">
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
                className="bg-[#0B0E14] border-white/10 text-base rounded-2xl"
                data-testid="settings-full-name"
              />
            </LabelledField>
            <LabelledField label="Date of Birth">
              <div className="relative">
                <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5D76E]" />
                <Input
                  type="date"
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  className="bg-[#0B0E14] border-white/10 text-base pl-10 rounded-2xl"
                  data-testid="settings-date-of-birth"
                />
              </div>
            </LabelledField>
            <Button onClick={handleSaveProfile} disabled={savingProfile} className="btn-primary w-full rounded-2xl" data-testid="save-profile-btn">
              {savingProfile ? <div className="spinner w-4 h-4" /> : 'Save Profile'}
            </Button>
          </SettingsPanel>
        )}

        {activePanel === 'phone' && (
          <SettingsPanel title="Phone Number" icon={Phone} testId="settings-phone-panel">
            <Input
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="+1 234 567 8900"
              className="bg-[#0B0E14] border-white/10 text-base rounded-2xl"
              data-testid="settings-phone"
            />
            <Button onClick={handleSavePhone} disabled={savingPhone} className="btn-primary w-full rounded-2xl" data-testid="save-phone-btn">
              {savingPhone ? <div className="spinner w-4 h-4" /> : phoneVerified ? 'Update Phone Number' : 'Save for Verification'}
            </Button>
          </SettingsPanel>
        )}

        {activePanel === 'password' && (
          <SettingsPanel title="Change Password" icon={Lock} testId="settings-password-panel">
            <PasswordField
              value={currentPassword}
              onChange={setCurrentPassword}
              show={showCurrentPw}
              setShow={setShowCurrentPw}
              placeholder="Current password"
              testId="current-password"
            />
            <PasswordField
              value={newPassword}
              onChange={setNewPassword}
              show={showNewPw}
              setShow={setShowNewPw}
              placeholder="New password"
              testId="new-password"
            />
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="bg-[#0B0E14] border-white/10 text-base rounded-2xl"
              data-testid="confirm-password"
            />
            <Button onClick={handleChangePassword} disabled={changingPassword} className="btn-primary w-full rounded-2xl" data-testid="change-password-btn">
              {changingPassword ? <div className="spinner w-4 h-4" /> : 'Change Password'}
            </Button>
          </SettingsPanel>
        )}
          </div>
        )}
      </div>
    </div>
  );
}

function SettingsOption({ icon: Icon, title, subtitle, status, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`vl-wood-setting-row w-full rounded-[22px] px-4 py-3 flex items-center gap-3 text-left transition anim-press ${active ? 'vl-wood-setting-row--active' : ''}`}
      data-testid={`settings-option-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="vl-wood-setting-row__icon w-10 h-10 rounded-xl text-[#F5D76E] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2">
          <span className="text-base font-black text-white">{title}</span>
          {status && (
            <span className="rounded-full px-2.5 py-0.5 text-[10px] font-black bg-[#D4AF37]/14 text-[#F5D76E]">
              {status}
            </span>
          )}
        </span>
        <span className="block text-xs text-gray-300 truncate">{subtitle}</span>
      </span>
      <ChevronRight className="w-4 h-4 text-[#F5D76E]" />
    </button>
  );
}

function SettingsPanel({ title, icon: Icon, testId, children }) {
  return (
    <section className="vl-wood-panel rounded-[24px] p-3.5 sm:p-4 space-y-3" data-testid={testId}>
      <h2 className="text-base font-black flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#F5D76E]" /> {title}
      </h2>
      {children}
    </section>
  );
}

function PasswordField({ value, onChange, show, setShow, placeholder, testId }) {
  return (
    <div className="relative">
      <Input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-[#0B0E14] border-white/10 text-base pr-12 rounded-2xl"
        data-testid={testId}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  );
}

function LabelledField({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-black uppercase tracking-[0.14em] text-gray-400">{label}</span>
      {children}
    </label>
  );
}
